import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { generateFakeMemberData } from "../../../../utils/fakeMemberData";
import InvoiceModal from "./InvoiceModal";

// Define the type for our member data
export interface MemberData {
  id: string;
  name: string;
  hasPaid: boolean;
  membershipFee: number;
  dateJoined: string;
  approved: boolean;
  membershipType: string;
  renewalDate: string;
}

// Define sort directions
type SortDirection = "asc" | "desc";

// Define column keys
type ColumnKey =
  | "name"
  | "hasPaid"
  | "approved"
  | "membershipFee"
  | "dateJoined"
  | "membershipType"
  | "renewalDate";

// Helper function to format dates consistently
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const ManageMembers = () => {
  // State for member data
  const [members, setMembers] = useState<MemberData[]>([]);

  // State for search query
  const [searchQuery, setSearchQuery] = useState("");

  // State for sorting
  const [sortColumn, setSortColumn] = useState<ColumnKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // State for the selected member and modal visibility
  const [selectedMember, setSelectedMember] = useState<MemberData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load fake data on component mount
  useEffect(() => {
    const data = generateFakeMemberData(173).map((member) => {
      // Generate random month (1-12)
      const randomMonth = Math.floor(Math.random() * 12) + 1;
      // Get last day of the random month in 2026
      const lastDayOfMonth = new Date(2026, randomMonth, 0).getDate();
      const renewalDate = new Date(
        2026,
        randomMonth - 1,
        lastDayOfMonth
      ).toISOString();

      return {
        ...member,
        membershipType: [
          "Company",
          "Non-Profit",
          "Independent/Retired Member",
          "Young Professional",
          "Associate Company",
        ][Math.floor(Math.random() * 5)],
        renewalDate,
      };
    });
    setMembers(data);
  }, []);

  // Handle opening the invoice modal
  const handleOpenInvoice = (member: MemberData) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  // Handle closing the invoice modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle sort
  const handleRequestSort = (column: ColumnKey) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortColumn(column);
  };

  // Filter members based on search query
  const filteredMembers = members.filter((member) =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort members based on current sort column and direction
  const sortedMembers = [...filteredMembers].sort((a, b) => {
    // If sorting by name, approved, hasPaid, use string comparison
    if (sortColumn === "name") {
      return sortDirection === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }

    // If sorting by hasPaid or approved, convert boolean to number
    if (sortColumn === "hasPaid") {
      const aVal = a.hasPaid ? 1 : 0;
      const bVal = b.hasPaid ? 1 : 0;
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    }

    if (sortColumn === "approved") {
      const aVal = a.approved ? 1 : 0;
      const bVal = b.approved ? 1 : 0;
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    }

    // If sorting by membershipFee
    if (sortColumn === "membershipFee") {
      return sortDirection === "asc"
        ? a.membershipFee - b.membershipFee
        : b.membershipFee - a.membershipFee;
    }

    // If sorting by dateJoined
    if (sortColumn === "dateJoined") {
      return sortDirection === "asc"
        ? new Date(a.dateJoined).getTime() - new Date(b.dateJoined).getTime()
        : new Date(b.dateJoined).getTime() - new Date(a.dateJoined).getTime();
    }

    // If sorting by membershipType
    if (sortColumn === "membershipType") {
      return sortDirection === "asc"
        ? a.membershipType.localeCompare(b.membershipType)
        : b.membershipType.localeCompare(a.membershipType);
    }

    // If sorting by renewalDate
    if (sortColumn === "renewalDate") {
      return sortDirection === "asc"
        ? new Date(a.renewalDate).getTime() - new Date(b.renewalDate).getTime()
        : new Date(b.renewalDate).getTime() - new Date(a.renewalDate).getTime();
    }

    return 0;
  });

  return (
    <div className="p-6">
      {/* Search bar */}
      <div className="mb-6 max-w-lg">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search members"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="text-gray-500" />
              </InputAdornment>
            ),
          }}
          className="bg-white"
        />
      </div>

      {/* Member table */}
      <TableContainer component={Paper} className="mb-8">
        <Table aria-label="member table">
          <TableHead className="bg-gray-50">
            <TableRow>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "name"}
                  direction={sortColumn === "name" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  Member
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "hasPaid"}
                  direction={sortColumn === "hasPaid" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("hasPaid")}
                >
                  Has Paid
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "approved"}
                  direction={sortColumn === "approved" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("approved")}
                >
                  Approved
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "membershipFee"}
                  direction={
                    sortColumn === "membershipFee" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("membershipFee")}
                >
                  Membership Fee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "dateJoined"}
                  direction={
                    sortColumn === "dateJoined" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("dateJoined")}
                  IconComponent={KeyboardArrowDownIcon}
                >
                  Date Joined
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "membershipType"}
                  direction={
                    sortColumn === "membershipType" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("membershipType")}
                >
                  Membership Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{
                    fontWeight: "bold",
                  }}
                  active={sortColumn === "renewalDate"}
                  direction={
                    sortColumn === "renewalDate" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("renewalDate")}
                >
                  Upcoming Renewal Date
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedMembers.map((member) => (
              <TableRow
                key={member.id}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <TableCell component="th" scope="row">
                  {member.name}
                </TableCell>
                <TableCell>
                  {member.hasPaid ? (
                    <div className="flex items-center">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Paid
                      </span>
                      <button
                        className="ml-2 text-blue-600 hover:underline"
                        onClick={() => handleOpenInvoice(member)}
                        aria-label={`Open invoice for ${member.name}`}
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleOpenInvoice(member)
                        }
                      >
                        Open Invoice
                      </button>
                    </div>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                      Unpaid
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {member.approved ? (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      Approved
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                      Pending
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  $
                  {member.membershipFee.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </TableCell>
                <TableCell>{formatDate(member.dateJoined)}</TableCell>
                <TableCell>{member.membershipType}</TableCell>
                <TableCell>{formatDate(member.renewalDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invoice Modal */}
      <InvoiceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        member={selectedMember}
      />
    </div>
  );
};

export default ManageMembers;
